<script setup lang="ts">
import { getLocalTimeZone, today } from "@internationalized/date";
import {
	CalendarCell,
	CalendarCellTrigger,
	CalendarGrid,
	CalendarGridBody,
	CalendarGridHead,
	CalendarGridRow,
	CalendarHeadCell,
	CalendarHeader,
	CalendarHeading,
	CalendarRoot,
	useDateFormatter,
	useForwardPropsEmits,
	type CalendarRootEmits,
	type CalendarRootProps,
} from "reka-ui";
import { createDecade, createYear, toDate } from "reka-ui/date";
import { shallowRef } from "vue";
import { useDelegatedProps, type ClassProps } from "../../lib/base-props";
import { cn } from "../../lib/utils";
import Button from "../button/Button.vue";
import { Select, SelectOption } from "../select";
import CalendarNextButton from "./CalendarNextButton.vue";
import CalendarPrevButton from "./CalendarPrevButton.vue";

// We can't use interface, since CalendarRootProps is an Union
type Props = Omit<CalendarRootProps, "placeholder"> &
	ClassProps & { timezone?: string; locale?: string };

const props = defineProps<Props>();
const emit = defineEmits<CalendarRootEmits>();

const formatter = useDateFormatter(props.locale || "en");

const placeholder = shallowRef(today(props.timezone ?? getLocalTimeZone()));

const delegatedProps = useDelegatedProps(props);
const forwarded = useForwardPropsEmits(delegatedProps, emit);

function updateMonth(monthStr: string | null) {
	if (monthStr == null || monthStr === "") {
		return;
	}

	const month = Number(monthStr);
	if (month === placeholder.value.month) {
		return;
	}

	placeholder.value = placeholder.value.set({ month });
}

function updateYear(yearStr: string | null) {
	if (yearStr == null || yearStr === "") {
		return;
	}

	const year = Number(yearStr);
	if (year === placeholder.value.year) {
		return;
	}

	placeholder.value = placeholder.value.set({ year });
}
</script>

<template>
	<CalendarRoot
		v-slot="{ date, grid, weekDays }"
		v-model:placeholder="placeholder"
		:class="cn('p-3', props.class)"
		v-bind="forwarded"
	>
		<CalendarHeader class="flex-center relative w-full pt-1">
			<CalendarHeading class="flex w-full items-center justify-between gap-2">
				<CalendarPrevButton />
				<Select
					class="w-[60%]"
					:defaultValue="placeholder.month.toString()"
					size="sm"
					@update:modelValue="updateMonth"
				>
					<SelectOption
						v-for="month in createYear({ dateObj: date })"
						:key="month.toString()"
						:value="month.month.toString()"
					>
						{{ formatter.custom(toDate(month), { month: "short" }) }}
					</SelectOption>
				</Select>

				<Select
					class="w-[40%]"
					:defaultValue="placeholder.year.toString()"
					size="sm"
					@update:modelValue="updateYear"
				>
					<SelectOption
						v-for="yearValue in createDecade({ dateObj: date, startIndex: -10, endIndex: 10 })"
						:key="yearValue.toString()"
						:value="yearValue.year.toString()"
					>
						{{ yearValue.year }}
					</SelectOption>
				</Select>
				<CalendarNextButton />
			</CalendarHeading>
		</CalendarHeader>

		<div class="mt-4 flex flex-col gap-y-4 sm:flex-row sm:gap-x-4 sm:gap-y-0">
			<CalendarGrid
				v-for="month of grid"
				:key="month.value.toString()"
				class="w-full border-collapse space-x-1"
			>
				<CalendarGridHead>
					<CalendarGridRow class="flex gap-1">
						<CalendarHeadCell
							v-for="day of weekDays"
							:key="day"
							class="text-muted-content w-8 rounded-md text-sm font-normal"
						>
							{{ day }}
						</CalendarHeadCell>
					</CalendarGridRow>
				</CalendarGridHead>
				<CalendarGridBody>
					<CalendarGridRow
						v-for="(weekDates, index) of month.rows"
						:key="`weekDate-${index}`"
						class="mt-2 flex w-full gap-1"
					>
						<CalendarCell
							v-for="weekDate of weekDates"
							:key="weekDate.toString()"
							class="calendar-cell"
							:date="weekDate"
						>
							<CalendarCellTrigger v-slot="slotProps" asChild :day="weekDate" :month="month.value">
								<Button class="calendar-cell-trigger" variant="ghost" size="sm">{{
									slotProps.dayValue
								}}</Button>
							</CalendarCellTrigger>
						</CalendarCell>
					</CalendarGridRow>
				</CalendarGridBody>
			</CalendarGrid>
		</div>
	</CalendarRoot>
</template>
